/**
 * Sound engine — síntese rica via Web Audio API.
 *
 * Versão 2: redesenho premium baseado em primitivas reutilizáveis:
 *  - bell(): 3 harmônicos (fundamental + 2x + 3x freq) com decays distintos
 *    → som de sino real, não sine seca
 *  - pluck(): noise burst + sine chirp → click acústico
 *  - whoosh(): bandpass noise com freq sweep → ar/movimento
 *  - chord(): múltiplas bells simultâneas (não arpejo) → acorde rico
 *  - reverb bus: delay loop com low-pass → tail espacial em todos os sons
 *    musicais
 *
 * Referências sonoras:
 *  - Bells: iPhone unlock "tink", iMessage receive
 *  - Cha-ching: cash register vintage com bell + coin cascade
 *  - Whoosh: transition do iOS Control Center
 *  - Reward: Apple Pay completion + Mario level-up
 *
 * Auto-init lazy + resume on user gesture (mobile autoplay policy).
 */

type SoundName =
  | 'tap'
  | 'pop'
  | 'success'
  | 'error'
  | 'notif'
  | 'chaching'
  | 'unlock'
  | 'tourStep'
  | 'reward';

const STORAGE_KEY = 'sixpass.soundEnabled';

class SoundEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private reverbSend: GainNode | null = null;
  private _enabled = true;
  private _unlockBound = false;

  constructor() {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        this._enabled = stored !== '0';
      } catch {
        this._enabled = true;
      }
      // Unlock no primeiro user gesture — necessário pra autoplay policy.
      // Se a 1a tentativa de play vier de um useEffect (não de clique direto),
      // o ctx pode ficar suspended pra sempre. Esse listener garante init.
      this.bindUnlock();
    }
  }

  private bindUnlock(): void {
    if (this._unlockBound || typeof window === 'undefined') return;
    this._unlockBound = true;
    const unlock = () => {
      // Cria o ctx (se ainda não existe) E faz resume — tudo dentro do gesture
      this.ensureCtx();
      if (this.ctx && this.ctx.state === 'suspended') {
        void this.ctx.resume();
      }
    };
    const opts: AddEventListenerOptions = { once: false, passive: true, capture: true };
    window.addEventListener('pointerdown', unlock, opts);
    window.addEventListener('touchstart', unlock, opts);
    window.addEventListener('keydown', unlock, opts);
    window.addEventListener('click', unlock, opts);
  }

  get enabled(): boolean {
    return this._enabled;
  }

  setEnabled(on: boolean): void {
    this._enabled = on;
    try {
      localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
    } catch {
      // localStorage pode falhar em private mode — ignora
    }
  }

  private ensureCtx(): AudioContext | null {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return this.ctx;
    }
    try {
      const AC =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      if (!AC) return null;
      this.ctx = new AC();
      // Resume imediato — se chamado de dentro de user gesture, funciona.
      // Se chamado fora, o listener de unlock vai pegar depois.
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.3; // volume global moderado
      this.master.connect(this.ctx.destination);

      // Reverb bus — delay loop com low-pass = cauda espacial
      this.reverbSend = this.ctx.createGain();
      this.reverbSend.gain.value = 1;
      const delay = this.ctx.createDelay(1);
      delay.delayTime.value = 0.085;
      const feedback = this.ctx.createGain();
      feedback.gain.value = 0.45;
      const filter = this.ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 2200;
      const wet = this.ctx.createGain();
      wet.gain.value = 0.22;

      this.reverbSend.connect(delay);
      delay.connect(filter);
      filter.connect(feedback);
      feedback.connect(delay);
      filter.connect(wet);
      wet.connect(this.master);

      return this.ctx;
    } catch (err) {
      console.warn('[sound] AudioContext init falhou', err);
      return null;
    }
  }

  // === Primitivas ===

  /** Sino: 3 harmônicos com decays diferentes. Som rico, parecido com tin/glass. */
  private bell(opts: {
    freq: number;
    durMs: number;
    gain?: number;
    delayMs?: number;
    reverb?: boolean;
  }): void {
    if (!this.ctx || !this.master) return;
    const t0 = this.ctx.currentTime + (opts.delayMs || 0) / 1000;
    const dur = opts.durMs / 1000;
    const peak = opts.gain ?? 0.18;
    const target = opts.reverb !== false ? this.reverbSend! : this.master;

    // Camada 1: fundamental (decay longo)
    const o1 = this.ctx.createOscillator();
    const g1 = this.ctx.createGain();
    o1.type = 'sine';
    o1.frequency.value = opts.freq;
    g1.gain.setValueAtTime(0, t0);
    g1.gain.linearRampToValueAtTime(peak, t0 + 0.003);
    g1.gain.exponentialRampToValueAtTime(0.0008, t0 + dur);
    o1.connect(g1).connect(this.master);
    g1.connect(target);
    o1.start(t0);
    o1.stop(t0 + dur + 0.05);

    // Camada 2: 2x freq (octave) — decay médio, mais brilho
    const o2 = this.ctx.createOscillator();
    const g2 = this.ctx.createGain();
    o2.type = 'sine';
    o2.frequency.value = opts.freq * 2;
    g2.gain.setValueAtTime(0, t0);
    g2.gain.linearRampToValueAtTime(peak * 0.4, t0 + 0.002);
    g2.gain.exponentialRampToValueAtTime(0.0008, t0 + dur * 0.55);
    o2.connect(g2).connect(this.master);
    o2.start(t0);
    o2.stop(t0 + dur * 0.55 + 0.05);

    // Camada 3: 3x freq — decay curto, transient sparkle
    const o3 = this.ctx.createOscillator();
    const g3 = this.ctx.createGain();
    o3.type = 'sine';
    o3.frequency.value = opts.freq * 3.01; // leve detune pra evitar phase lock
    g3.gain.setValueAtTime(0, t0);
    g3.gain.linearRampToValueAtTime(peak * 0.15, t0 + 0.002);
    g3.gain.exponentialRampToValueAtTime(0.0008, t0 + dur * 0.25);
    o3.connect(g3).connect(this.master);
    o3.start(t0);
    o3.stop(t0 + dur * 0.25 + 0.05);
  }

  /** Pluck: noise burst curto + sine chirp. Bom pra click/tap. */
  private pluck(opts: {
    freq: number;
    durMs: number;
    gain?: number;
    delayMs?: number;
  }): void {
    if (!this.ctx || !this.master) return;
    const t0 = this.ctx.currentTime + (opts.delayMs || 0) / 1000;
    const dur = opts.durMs / 1000;
    const peak = opts.gain ?? 0.16;

    // Noise burst (impacto)
    const bufSize = Math.floor(this.ctx.sampleRate * 0.02);
    const buf = this.ctx.createBuffer(1, bufSize, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufSize, 2);
    }
    const noise = this.ctx.createBufferSource();
    noise.buffer = buf;
    const nFilter = this.ctx.createBiquadFilter();
    nFilter.type = 'bandpass';
    nFilter.frequency.value = opts.freq * 2;
    nFilter.Q.value = 3;
    const nGain = this.ctx.createGain();
    nGain.gain.value = peak * 0.4;
    noise.connect(nFilter).connect(nGain).connect(this.master);
    noise.start(t0);

    // Sine chirp pra dar corpo
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = 'triangle';
    o.frequency.setValueAtTime(opts.freq * 1.5, t0);
    o.frequency.exponentialRampToValueAtTime(opts.freq, t0 + dur);
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(peak, t0 + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0008, t0 + dur);
    o.connect(g).connect(this.master);
    o.start(t0);
    o.stop(t0 + dur + 0.02);
  }

  /**
   * Coin ping: simula impacto metálico real.
   * Real coin = partials INHARMÔNICOS (não múltiplos exatos) + transient
   * de noise + decay rápido. Sino musical (bell()) tem partials 1:2:3 que
   * soa "musical/mágico". Moeda usa ratios típicos de metal (1 : 2.76 : 5.4).
   */
  private coinPing(opts: {
    freq: number;
    gain?: number;
    delayMs?: number;
  }): void {
    if (!this.ctx || !this.master) return;
    const t0 = this.ctx.currentTime + (opts.delayMs || 0) / 1000;
    const peak = opts.gain ?? 0.14;
    const dur = 0.18; // moeda decai rápido

    // 1) Transient noise burst — som do impacto físico (5ms)
    const bufSize = Math.floor(this.ctx.sampleRate * 0.008);
    const buf = this.ctx.createBuffer(1, bufSize, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufSize, 1.5);
    }
    const noise = this.ctx.createBufferSource();
    noise.buffer = buf;
    const nFilter = this.ctx.createBiquadFilter();
    nFilter.type = 'highpass';
    nFilter.frequency.value = opts.freq * 0.8;
    const nGain = this.ctx.createGain();
    nGain.gain.value = peak * 0.5;
    noise.connect(nFilter).connect(nGain).connect(this.master);
    nGain.connect(this.reverbSend!);
    noise.start(t0);

    // 2) Partials inharmônicos — assinatura sonora de metal
    // Ratios escolhidos pra ter aquela "shimmer" desafinada de moeda real
    const partials = [
      { ratio: 1.0, gain: 1.0, decay: 1.0 },
      { ratio: 2.76, gain: 0.55, decay: 0.45 },
      { ratio: 5.4, gain: 0.32, decay: 0.22 },
      { ratio: 8.93, gain: 0.18, decay: 0.12 },
    ];
    partials.forEach((p) => {
      const o = this.ctx!.createOscillator();
      const g = this.ctx!.createGain();
      o.type = 'sine';
      o.frequency.value = opts.freq * p.ratio;
      g.gain.setValueAtTime(0, t0);
      g.gain.linearRampToValueAtTime(peak * p.gain, t0 + 0.001);
      g.gain.exponentialRampToValueAtTime(0.0005, t0 + dur * p.decay);
      o.connect(g);
      g.connect(this.master);
      g.connect(this.reverbSend!);
      o.start(t0);
      o.stop(t0 + dur * p.decay + 0.05);
    });
  }

  /** Whoosh: bandpass noise com freq sweep + chirp grave. */
  private whoosh(opts: { durMs: number; gain?: number; delayMs?: number }): void {
    if (!this.ctx || !this.master) return;
    const t0 = this.ctx.currentTime + (opts.delayMs || 0) / 1000;
    const dur = opts.durMs / 1000;
    const peak = opts.gain ?? 0.12;

    const bufSize = Math.floor(this.ctx.sampleRate * dur);
    const buf = this.ctx.createBuffer(1, bufSize, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) data[i] = Math.random() * 2 - 1;
    const noise = this.ctx.createBufferSource();
    noise.buffer = buf;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(800, t0);
    filter.frequency.exponentialRampToValueAtTime(4500, t0 + dur * 0.7);
    filter.frequency.exponentialRampToValueAtTime(1200, t0 + dur);
    filter.Q.value = 2.5;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(peak, t0 + dur * 0.3);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + dur);

    noise.connect(filter).connect(gain).connect(this.reverbSend!);
    gain.connect(this.master);
    noise.start(t0);
    noise.stop(t0 + dur + 0.02);

    // Chirp grave por baixo pra dar peso
    const o = this.ctx.createOscillator();
    const og = this.ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(200, t0);
    o.frequency.exponentialRampToValueAtTime(800, t0 + dur);
    og.gain.setValueAtTime(0, t0);
    og.gain.linearRampToValueAtTime(peak * 0.6, t0 + dur * 0.2);
    og.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    o.connect(og).connect(this.master);
    o.start(t0);
    o.stop(t0 + dur + 0.02);
  }

  // === API pública ===

  play(name: SoundName): void {
    if (!this._enabled) return;
    if (!this.ensureCtx()) return;

    switch (name) {
      case 'tap':
        // Click curto: pluck agudo
        this.pluck({ freq: 1400, durMs: 70, gain: 0.13 });
        break;

      case 'pop':
        // Pop líquido: pluck + bell octave
        this.pluck({ freq: 900, durMs: 80, gain: 0.14 });
        this.bell({ freq: 1800, durMs: 200, gain: 0.08, delayMs: 20 });
        break;

      case 'success':
        // Acorde C maior simultâneo (todas notas juntas, não arpejo)
        // C5 + E5 + G5 com leve stagger pra "chorus" feel
        this.bell({ freq: 523.25, durMs: 700, gain: 0.16 });
        this.bell({ freq: 659.25, durMs: 700, gain: 0.14, delayMs: 15 });
        this.bell({ freq: 783.99, durMs: 700, gain: 0.12, delayMs: 30 });
        // Sparkle agudo
        this.bell({ freq: 1567.98, durMs: 350, gain: 0.06, delayMs: 80 });
        break;

      case 'error':
        // Bell grave + sawtooth descendente (dry, sem reverb)
        if (this.ctx && this.master) {
          const t0 = this.ctx.currentTime;
          const o = this.ctx.createOscillator();
          const g = this.ctx.createGain();
          o.type = 'sawtooth';
          o.frequency.setValueAtTime(280, t0);
          o.frequency.exponentialRampToValueAtTime(140, t0 + 0.25);
          // Low-pass pra suavizar
          const flt = this.ctx.createBiquadFilter();
          flt.type = 'lowpass';
          flt.frequency.value = 800;
          g.gain.setValueAtTime(0, t0);
          g.gain.linearRampToValueAtTime(0.12, t0 + 0.01);
          g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.28);
          o.connect(flt).connect(g).connect(this.master);
          o.start(t0);
          o.stop(t0 + 0.3);
        }
        break;

      case 'notif':
        // 2 bells suaves E5 + G5
        this.bell({ freq: 659.25, durMs: 500, gain: 0.13 });
        this.bell({ freq: 783.99, durMs: 600, gain: 0.13, delayMs: 110 });
        break;

      case 'chaching': {
        // Cha-ching = sino de caixa registradora + chuva DE MOEDAS METÁLICAS.
        // Diferença chave do bell musical: coinPing usa partials INHARMÔNICOS
        // (ratios 1 : 2.76 : 5.4 : 8.93) → soa metálico/desafinado como moeda
        // real, não como sino de igreja.

        // 1) "Ting!" de abertura — sino agudo brilhante (caixa registradora)
        this.bell({ freq: 2093, durMs: 700, gain: 0.18 });
        this.bell({ freq: 2637, durMs: 350, gain: 0.1, delayMs: 8 });

        // 2) Chuva de moedas metálicas — pings inharmônicos espalhados
        // Frequências base em região de moeda (1.2k–2.5k Hz) com slight random
        const coinBaseFreqs = [1400, 1650, 1850, 1500, 2100, 1750, 1300, 1950, 1600, 1420];
        coinBaseFreqs.forEach((f, i) => {
          this.coinPing({
            freq: f + (Math.random() - 0.5) * 100, // jitter pra evitar identical pings
            gain: 0.1 + Math.random() * 0.04,
            delayMs: 60 + i * 45 + Math.random() * 30,
          });
        });

        // 3) Pings extras mais espalhados (cauda longa de moedas caindo)
        for (let i = 0; i < 5; i++) {
          this.coinPing({
            freq: 1200 + Math.random() * 900,
            gain: 0.08 + Math.random() * 0.04,
            delayMs: 500 + i * 80 + Math.random() * 100,
          });
        }

        // 4) Bell de fechamento (C5 grave dá fundo musical sem competir)
        this.bell({ freq: 523.25, durMs: 600, gain: 0.09, delayMs: 250 });
        break;
      }

      case 'unlock':
        // Fanfare arpejo C-E-G-C + sparkle
        this.bell({ freq: 523.25, durMs: 400, gain: 0.14 });
        this.bell({ freq: 659.25, durMs: 400, gain: 0.14, delayMs: 110 });
        this.bell({ freq: 783.99, durMs: 400, gain: 0.14, delayMs: 220 });
        this.bell({ freq: 1046.5, durMs: 700, gain: 0.16, delayMs: 330 });
        // Sparkle sobre a última
        this.bell({ freq: 2093, durMs: 300, gain: 0.08, delayMs: 380 });
        this.bell({ freq: 2637, durMs: 220, gain: 0.06, delayMs: 450 });
        break;

      case 'tourStep':
        // Whoosh suave + bell pequeno na chegada
        this.whoosh({ durMs: 220, gain: 0.09 });
        this.bell({ freq: 1568, durMs: 220, gain: 0.08, delayMs: 130 });
        break;

      case 'reward':
        // Final do tour: acorde maior cheio + sparkle cascata
        this.bell({ freq: 523.25, durMs: 1000, gain: 0.15 });
        this.bell({ freq: 659.25, durMs: 1000, gain: 0.14, delayMs: 20 });
        this.bell({ freq: 783.99, durMs: 1000, gain: 0.13, delayMs: 40 });
        this.bell({ freq: 1046.5, durMs: 800, gain: 0.12, delayMs: 60 });
        // Sparkle cascade
        [1567, 1975, 2349, 2637, 3136].forEach((f, i) => {
          this.bell({ freq: f, durMs: 200, gain: 0.06, delayMs: 200 + i * 80 });
        });
        break;
    }
  }
}

const engine = new SoundEngine();

export function playSound(name: SoundName): void {
  engine.play(name);
}

export function setSoundEnabled(on: boolean): void {
  engine.setEnabled(on);
}

export function isSoundEnabled(): boolean {
  return engine.enabled;
}

export type { SoundName };
